import { FC } from 'react';

import { Flex, Tag, Typography } from 'antd';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconClock from '@/assets/icons/icon-clock-circle.svg?react';
import IconID from '@/assets/icons/icon-id.svg?react';
import IconName from '@/assets/icons/icon-name.svg?react';
import IconPlants from '@/assets/icons/icon-plants.svg?react';
import IconScenarios from '@/assets/icons/icon-scenarios.svg?react';
import IconStorageSystems from '@/assets/icons/icon-storage-systems.svg?react';
import Card from '@/components/RCard';
import { SimulationResult } from '@/types/simulations';
import { formatDate } from '@/utils/index';

type ParametersProps = {
  simulationData: SimulationResult;
};

const Parameters: FC<ParametersProps> = ({ simulationData }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Card
      title={
        <Flex gap={24}>
          <Typography.Title level={3} className="fs-17-bold">
            {t('components.simReport.parameters.title')}
          </Typography.Title>
          <Flex gap={8} align="center">
            <Typography.Text className="fs-14-medium text-faded-color">
              {t('components.simReport.parameters.fields.id')}
            </Typography.Text>
            <IconID fill={theme.colors.grayAccent} />
            <Typography.Text className="fs-14-regular ">
              {simulationData?.id}
            </Typography.Text>
          </Flex>
          <Flex gap={8} align="center">
            <Typography.Text className="fs-14-medium text-faded-color">
              {t('components.simReport.parameters.fields.name')}
            </Typography.Text>
            <IconName fill={theme.colors.grayAccent} />
            <Typography.Text
              className="fs-14-regular"
              style={{ maxWidth: '150px' }}
              ellipsis={{
                tooltip: simulationData?.name,
              }}
            >
              {simulationData?.name}
            </Typography.Text>
          </Flex>
          <Flex gap={8} align="center">
            <Typography.Text className="fs-14-medium text-faded-color">
              {t('components.simReport.parameters.fields.startDate')}
            </Typography.Text>
            <IconClock fill={theme.colors.grayAccent} />
            <Typography.Text className="fs-14-regular ">
              {formatDate(simulationData?.startDate || '')}
            </Typography.Text>
          </Flex>
          <Flex gap={8} align="center">
            <Typography.Text className="fs-14-medium text-faded-color">
              {t('components.simReport.parameters.fields.endDate')}
            </Typography.Text>
            <IconClock fill={theme.colors.grayAccent} />
            <Typography.Text className="fs-14-regular">
              {formatDate(simulationData?.endDate || '')}
            </Typography.Text>
          </Flex>
          <Flex gap={8} align="center">
            <Typography.Text className="fs-14-medium text-faded-color">
              {t('components.simReport.parameters.fields.resultDate')}
            </Typography.Text>
            <IconClock fill={theme.colors.grayAccent} />
            <Typography.Text className="fs-14-regular ">
              {formatDate(simulationData?.dateDone || '')}
            </Typography.Text>
          </Flex>
        </Flex>
      }
      $paddingBody="32px 24px"
      background="transparent"
    >
      <Flex gap={72} align="center">
        <Flex gap={24} align="center">
          <Flex gap={8} align="center">
            <IconPlants
              fill={theme.colors.grayAccent}
              style={{ height: '32px', width: 'auto' }}
            />
            <Typography.Text className="fs-17-semi-bold faded-text">
              {t('components.simReport.parameters.assets.plant')}
            </Typography.Text>
          </Flex>{' '}
          <Flex gap={12} align="center">
            {simulationData?.plants.map((plant) => (
              <motion.div key={plant.id} whileTap={{ scale: 0.95 }}>
                <Tag className="tags" key={plant.id}>
                  {plant.name.length > 20
                    ? `${plant.name.substring(0, 17)}...`
                    : plant.name}
                </Tag>
              </motion.div>
            ))}
          </Flex>
        </Flex>
        <Flex gap={24} align="center">
          <Flex gap={8} align="center">
            <IconScenarios
              fill={theme.colors.grayAccent}
              style={{ height: '32px', width: 'auto' }}
            />
            <Typography.Text className="fs-17-semi-bold faded-text">
              {t('components.simReport.parameters.assets.scenario')}
            </Typography.Text>
          </Flex>
          <motion.div whileTap={{ scale: 0.95 }}>
            <Tag className="tags">
              {simulationData && simulationData?.scenario.name.length > 20
                ? `${simulationData.scenario.name.substring(0, 17)}...`
                : simulationData?.scenario.name}
            </Tag>
          </motion.div>
        </Flex>
        <Flex gap={24} align="center">
          <Flex gap={8} align="center">
            <IconStorageSystems
              fill={theme.colors.grayAccent}
              style={{ height: '32px', width: 'auto' }}
            />
            <Typography.Text className="fs-17-semi-bold faded-text">
              {t('components.simReport.parameters.assets.storageSystem')}
            </Typography.Text>
          </Flex>
          <motion.div whileTap={{ scale: 0.95 }}>
            <Tag className="tags">
              {simulationData && simulationData?.storage.name.length > 20
                ? `${simulationData.storage.name.substring(0, 17)}...`
                : simulationData?.storage.name}
            </Tag>
          </motion.div>
        </Flex>
      </Flex>
    </Card>
  );
};

export default Parameters;
